import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { fixedAssetsApi } from './api'
import type { CreateSchedulePayload } from './types'

export function useSchedules() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'depreciation-schedules'),
    queryFn: fixedAssetsApi.list,
  })
}

export function useScheduleDetail(id: number | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'depreciation-schedules', id ?? 'none', 'detail'),
    queryFn: () => fixedAssetsApi.detail(id!),
    enabled: id !== null,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>, tenantId: string | null) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'depreciation-schedules') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'posting-status') })
}

export function useCreateSchedule() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: CreateSchedulePayload) => fixedAssetsApi.create(body),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useUpdateSchedule() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<CreateSchedulePayload> }) =>
      fixedAssetsApi.update(id, body),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useDeleteSchedule() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => fixedAssetsApi.remove(id),
    onSuccess: () => invalidate(qc, tenantId),
  })
}
